/**
 * @fileoverview RelatedNews component for displaying related news articles with pagination
 * 
 * @see {@link src/components/news/index.tsx} - Main news detail component
 * @see {@link src/components/news/relatedNews/RelatedNewsItem.jsx} - Individual news item renderer
 * @see {@link src/components/ui/Card.jsx} - Card wrapper component
 */

import { useState } from "react";

import { Flex, Button, Text } from "@chakra-ui/react";
import { useParams } from "next/navigation";

import { axiosService } from "@/services/apiService/axiosService";

import Card from "../../ui/Card";

import RelatedNewsItem from "./RelatedNewsItem";

/**
 * @typedef {Object} NewsItem
 * @property {string} id - Unique identifier for the news item
 * @property {string} title - Title of the news article
 * @property {string} slug - URL-friendly version of the title
 * @property {string} [description] - Brief description of the news article
 * @property {string} [publishedAt] - Publication date of the article
 * @property {string} [newsImageThumbnail] - URL of the article's thumbnail image
 */

/**
 * @typedef {Object} RelatedNewsResponse
 * @property {NewsItem[]} newsItems - Array of related news items
 * @property {number} currentPage - Current page number
 * @property {number} totalPages - Total number of pages available
 */

/**
 * @typedef {Object} RelatedNewsProps
 * @property {NewsItem} oneNews - The current news article
 * @property {RelatedNewsResponse|NewsItem[]} relatedNews - Related news data including pagination info or array of news items
 */

/**
 * Displays a list of related news articles with pagination support
 * 
 * @param {RelatedNewsProps} props - Component props
 * @returns {JSX.Element|null} Related news component or null if no related news
 * 
 * @see {@link src/components/ui/Card} - Card component used for layout
 * @see {@link src/components/news/relatedNews/RelatedNewsItem} - Individual news item component
 */
const RelatedNews = ({ oneNews, relatedNews }) => {
  // Handle both array and object structures
  const isNewsItemsArray = Array.isArray(relatedNews);
  // Ensure we always have an array of news items
  const newsItems = isNewsItemsArray ? relatedNews : (Array.isArray(relatedNews?.newsItems) ? relatedNews?.newsItems : []);
  const currentPage = isNewsItemsArray ? 1 : relatedNews?.currentPage || 1;
  
  const [relatedNewsItems, setRelatedNewsItems] = useState(newsItems || []);
  const [currentRelatedPage, setCurrentRelatedPage] = useState(parseInt(currentPage));
  const params = useParams();

  /**
   * Fetches the next page of related news articles
   * @returns {Promise<NewsItem[]|undefined>} Array of news items or undefined if error
   */
  const loadMoreRelatedNews = async () => {
    try {
      const response = await axiosService.get("/res/news/related", {
        params: {
          id: oneNews?.id,
          page: parseInt(currentRelatedPage) + 1,
          limit: 3,
        },
      });

      if (response && response.data && response.data.newsItems) {
        setRelatedNewsItems([...relatedNewsItems, ...response.data.newsItems]);
        setCurrentRelatedPage((prev) => parseInt(prev) + 1);
        return response.data.newsItems;
      }
    } catch (error) {
      console.error('Error fetching related news:', error);
    }
  };

  // Ensure relatedNewsItems is an array before filtering
  // Filter out the current article from related news
  const filteredNewsItems = Array.isArray(relatedNewsItems) 
    ? relatedNewsItems.filter((item) => item?.slug !== params?.news)
    : [];

  return (
    <>
      {filteredNewsItems?.length ? (
        <Card cardTitle={"Related Articles"}>
          <Flex flexDir={"column"}>
            {filteredNewsItems?.map((newsItem) => (
              <RelatedNewsItem key={newsItem?.id} news={newsItem} />
            ))}
            {/* Get total pages count, handling both array and object structures */}
            {currentRelatedPage + 1 <= (isNewsItemsArray ? Math.ceil(relatedNews.length / 4) : relatedNews?.totalPages) ? (
              <Button variant={"unstyled"}>
                <Text
                  fontSize={"14px"}
                  fontWeight={"700"}
                  lineHeight={"1"}
                  color={"grey"}
                  pointerEvents={
                    currentRelatedPage + 1 <= (isNewsItemsArray ? Math.ceil(relatedNews.length / 4) : relatedNews?.totalPages) ? "auto" : "none"
                  }
                  onClick={() =>
                    currentRelatedPage + 1 <= (isNewsItemsArray ? Math.ceil(relatedNews.length / 4) : relatedNews?.totalPages)
                      ? loadMoreRelatedNews()
                      : null
                  }
                >
                  See More
                </Text>
              </Button>
            ) : null}
          </Flex>
        </Card>
      ) : null}
    </>
  );
};

export default RelatedNews;
